import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { X, Printer, Calendar, Award, TrendingDown, Stethoscope, BrainCircuit, Info, FileText } from 'lucide-react';
import useAppStore from '../store/appStore';
import MitarbeiterGespraechModal from './mitarbeiterGespraech/MitarbeiterGespraechModal';

// ==========================================
// TOOLTIP KOMPONENTE (GOLD / SCHWARZ)
// ==========================================
const CustomTooltip = ({ children, text, customClass = "" }: { children: React.ReactNode, text: string, customClass?: string }) => (
    <div className={`group relative flex flex-col w-full h-full cursor-help ${customClass}`}>
        {children}
        <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-64 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:-translate-y-1 z-[9999] print:hidden">
            <div className="bg-[#E6C299] text-black text-xs font-medium p-3 rounded-lg shadow-[0_10px_25px_-5px_rgba(0,0,0,0.5)] border border-[#c4a077] leading-relaxed text-center">
                {text}
            </div>
            <div className="w-3 h-3 bg-[#E6C299] border-b border-r border-[#c4a077] transform rotate-45 absolute -bottom-1.5 left-1/2 -translate-x-1/2"></div>
        </div>
    </div>
);

const safeDiv = (numerator: number, denominator: number): number => {
    const num = Number(numerator);
    const den = Number(denominator);
    if (!den || isNaN(num) || isNaN(den)) return 0;
    return num / den;
};

const formatCurrency = (value: number, currency = 'EUR', decimals = 0): string => {
    return new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value || 0) + ` ${currency}`;
};

const formatPercent = (value: number, decimals = 1): string => {
    return (value * 100).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }) + '%';
};

/** Differenz in Prozentpunkten mit Vorzeichen, z.B. "-5,8 Pkt." */
const formatProzentPunkte = (value: number, decimals = 1): string => {
    const pkt = value * 100;
    return `${pkt >= 0 ? '+' : ''}${pkt.toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    })} Pkt.`;
};

const formatNumber = (value: number, decimals = 1): string => {
    return Number(value).toLocaleString('de-DE', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });
};

export interface NormalizedMonthData {
    monatName: string;
    index: number; 
    bedienumsatzBrutto: number;
    vkUmsatzBrutto: number;
    gesamtBrutto: number;
    gesamtNetto: number;
    mindestNetto: number; 
    kostendeckungNetto: number;
    bonusBedienung: number;
    bonusVK: number;
    isLocked: boolean;         
    activeLohnfaktor: number;
    urlaub: number; 
    krank: number;
}

interface ExecutiveSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string; 
    currencyCode: string;
    mwstFaktor: number;
    auslastungsVorgabe?: number; 
    stammdaten: {
        wochenstunden: number;
        arbeitstage: number;
        monatsStunden: number;
        grundlohn: number;
        /** Effektiv-Brutto (Grundgehalt + LZK) — gleicher Nenner wie lohnfaktorSoll */
        effektiverBruttolohn?: number;
        gesamtlohnBrutto: number;
        stdLohnGrund: number;
        stdLohnGesamt: number;
        lohnfaktorSoll: number;
        kostendeckungsFaktor: number;
        salonStundensatz?: number; 
    };
    rawMonthsData: NormalizedMonthData[];
    isHJ2Freigegeben?: boolean; 
    teamEffizienzIst?: number;
}

type EvalStyle = {
    label: string;
    textClass: string;
    borderClass: string;
    bgClass: string;
    badgeBg: string;
    shadowClass: string;
    barClass: string;
};

const EVAL_STYLES: Record<1 | 2 | 3 | 4 | 5 | 6, EvalStyle> = {
    1: { label: 'Exzellent', textClass: 'text-purple-700 dark:text-purple-400 led-purple print:text-purple-700', borderClass: 'border-purple-500/50 print:border-purple-300', bgClass: 'bg-purple-50 dark:bg-purple-950/20 print:bg-purple-50', badgeBg: 'bg-purple-100 dark:bg-purple-950/40 border border-purple-500 print:bg-white', shadowClass: 'drop-shadow-[0_0_8px_rgba(168,85,247,0.5)] print:drop-shadow-none', barClass: 'bg-purple-500 shadow-[0_0_12px_rgba(168,85,247,0.45)]' },
    2: { label: 'Sehr Gut', textClass: 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-emerald-700', borderClass: 'border-emerald-500/50 print:border-emerald-300', bgClass: 'bg-emerald-50 dark:bg-emerald-950/20 print:bg-emerald-50', badgeBg: 'bg-emerald-100 dark:bg-emerald-950/40 border border-emerald-500 print:bg-white', shadowClass: '', barClass: 'bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]' },
    3: { label: 'Gut', textClass: 'text-lime-700 dark:text-lime-400 led-lime print:text-lime-700', borderClass: 'border-lime-500/50 print:border-lime-300', bgClass: 'bg-lime-50 dark:bg-lime-950/20 print:bg-lime-50', badgeBg: 'bg-lime-100 dark:bg-lime-950/40 border border-lime-500 print:bg-white', shadowClass: '', barClass: 'bg-lime-500 shadow-[0_0_12px_rgba(132,204,22,0.35)]' },
    4: { label: 'Grenzwertig Okay', textClass: 'text-amber-700 dark:text-amber-400 led-amber print:text-amber-700', borderClass: 'border-amber-500/50 print:border-amber-300', bgClass: 'bg-amber-50 dark:bg-amber-950/20 print:bg-amber-50', badgeBg: 'bg-amber-100 dark:bg-amber-950/40 border border-amber-500 print:bg-white', shadowClass: '', barClass: 'bg-amber-400 shadow-[0_0_12px_rgba(245,158,11,0.35)]' },
    5: { label: 'Kritisch', textClass: 'text-orange-700 dark:text-orange-400 led-orange print:text-orange-700', borderClass: 'border-orange-500/50 print:border-orange-300', bgClass: 'bg-orange-50 dark:bg-orange-950/20 print:bg-orange-50', badgeBg: 'bg-orange-100 dark:bg-orange-950/40 border border-orange-500 print:bg-white', shadowClass: '', barClass: 'bg-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.35)]' },
    6: { label: 'Defizitär', textClass: 'text-red-700 dark:text-red-400 led-red print:text-red-700', borderClass: 'border-red-500/50 print:border-red-300', bgClass: 'bg-red-50 dark:bg-red-950/20 print:bg-red-50', badgeBg: 'bg-red-100 dark:bg-red-950/40 border border-red-500 print:bg-white', shadowClass: '', barClass: 'bg-red-500 shadow-[0_0_12px_rgba(239,68,68,0.4)]' },
};

const getEvaluationLevel = (value: number, type: 'effizienz' | 'auslastung' | 'vk'): EvalStyle => {
    if (type === 'vk') {
        if (value >= 0.15) return { ...EVAL_STYLES[1], barClass: 'bg-purple-500' };
        if (value >= 0.10) return { ...EVAL_STYLES[2], barClass: 'bg-emerald-500' };
        if (value >= 0.069) return { ...EVAL_STYLES[3], barClass: 'bg-lime-500' };
        if (value >= 0.05) return { ...EVAL_STYLES[4], textClass: 'text-amber-700 dark:text-amber-400 led-amber print:text-yellow-700', borderClass: 'border-amber-500/50 print:border-yellow-300', bgClass: 'bg-amber-50 dark:bg-amber-950/20 print:bg-yellow-50', label: 'Akzeptabel', barClass: 'bg-amber-400' };
        if (value >= 0.03) return { ...EVAL_STYLES[5], barClass: 'bg-orange-500' };
        return { ...EVAL_STYLES[6], barClass: 'bg-red-500' };
    }

    let level: 1 | 2 | 3 | 4 | 5 | 6 = 6;
    if (type === 'auslastung') {
        if (value >= 0.85) level = 1;
        else if (value >= 0.75) level = 2;
        else if (value >= 0.70) level = 3;
        else if (value >= 0.60) level = 4;
        else if (value >= 0.50) level = 5;
    } else if (type === 'effizienz') {
        if (value >= 1.00) level = 1;
        else if (value >= 0.95) level = 2;
        else if (value >= 0.89) level = 3;
        else if (value >= 0.72) level = 4;
        else if (value >= 0.63) level = 5;
    }

    return EVAL_STYLES[level];
};

/**
 * Lohnfaktor-Ampel: Vorzeichen = Zielerreichung.
 * Unter Setup-Faktor → nie „Sehr Gut“/grün (wäre fachlich „überboten“).
 * Ab Setup-Faktor → positiv; darunter Amber/Orange/Rot je nach Abstand.
 */
const getLohnfaktorLevel = (ist: number, soll: number): EvalStyle => {
    const ratio = safeDiv(ist, soll || 1);
    if (ratio >= 1.05) return EVAL_STYLES[1];
    if (ratio >= 1.00) return EVAL_STYLES[2];
    if (ratio >= 0.95) return { ...EVAL_STYLES[4], label: 'Knapp verfehlt' };
    if (ratio >= 0.89) return { ...EVAL_STYLES[5], label: 'Unter Ziel' };
    if (ratio >= 0.72) return EVAL_STYLES[5];
    return EVAL_STYLES[6];
};

const KICard = ({ type, title, children, icon }: { type: string, title: string, children: React.ReactNode, icon: string }) => {
    let bgClass = 'bg-pe-card border-pe-border-strong text-pe-text dark:text-slate-100'; 
    let titleClass = 'text-amber-700 dark:text-amber-400 led-amber';
    
    if (type === 'excellent' || type === 'success') {
        bgClass = 'bg-emerald-50 dark:bg-emerald-950/20 border-emerald-500/40 text-pe-text dark:text-slate-100'; 
        titleClass = 'text-emerald-700 dark:text-emerald-400 led-emerald';
    } else if (type === 'warning' || type === 'critical' || type === 'danger') {
        bgClass = 'bg-red-50 dark:bg-red-950/20 border-red-500/40 text-pe-text dark:text-slate-100'; 
        titleClass = 'text-red-700 dark:text-red-400 led-red';
    }

    return (
        <div className={`p-5 rounded-xl border ${bgClass} shadow-sm print:bg-white print:border-slate-300 print:text-black`}>
            <div className="flex items-start gap-4">
                <span className="text-xl mt-0.5">{icon}</span>
                <div>
                    <h4 className={`font-bold text-base mb-1.5 print:text-black ${titleClass}`}>{title}</h4>
                    <div className="text-sm leading-relaxed opacity-95">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExecutiveSummaryModal: React.FC<ExecutiveSummaryModalProps> = ({
    isOpen,
    onClose,
    title,
    currencyCode,
    mwstFaktor,
    auslastungsVorgabe = 75,
    stammdaten,
    rawMonthsData,
    isHJ2Freigegeben = false,
    teamEffizienzIst
}) => {
    const [mounted, setMounted] = useState(false);
    const [startMonth, setStartMonth] = useState<number>(0);
    const [endMonth, setEndMonth] = useState<number>(5); 
    const [vereinbarungText, setVereinbarungText] = useState('');
    const [showAiAnalysis, setShowAiAnalysis] = useState(false);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiError, setAiError] = useState<string | null>(null);
    const [aiDiagnostics, setAiDiagnostics] = useState<any[]>([]);
    /** false = Admin-Detail, true = vereinfachte Mitarbeiter-Ansicht */
    const [isSimpleView, setIsSimpleView] = useState(false);
    const [isMgOpen, setIsMgOpen] = useState(false);

    const inhaberName = useAppStore((s) => {
        const cd = s.clientData || ({} as any);
        return String(cd.unternehmername || cd.salonname || cd.firmenname || '').trim();
    });

    const monateNamen = [
        'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 
        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];

    const maxSelectableMonth = isHJ2Freigegeben ? 11 : 5;

    const validMonths = useMemo(() => {
        const valid = rawMonthsData
            .filter(m => (m.gesamtBrutto > 0 || m.isLocked) && m.index <= maxSelectableMonth)
            .map(m => m.index);
        return valid.length > 0 ? valid : [0]; 
    }, [rawMonthsData, maxSelectableMonth]);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (isOpen && validMonths.length > 0) {
            setStartMonth(validMonths[0]);
            setEndMonth(validMonths[validMonths.length - 1]);
            setShowAiAnalysis(false);
            setAiError(null);
            setAiDiagnostics([]);
        }
    }, [isOpen, validMonths]);

    const analysis = useMemo(() => {
        const activeMonths = rawMonthsData.filter(
            m => m.index >= startMonth && m.index <= endMonth && (m.gesamtBrutto > 0 || m.isLocked)
        );
        
        const count = activeMonths.length || 1;

        let totalBedienBrutto = 0; let totalVkBrutto = 0; let totalGesamtBrutto = 0;
        let totalGesamtNetto = 0; let totalSollNetto = 0; let totalKostenNetto = 0;
        let totalBonusBedienung = 0; let totalBonusVK = 0;
        let lockedCount = 0; let totalActiveLohnfaktor = 0;
        let totalUrlaub = 0; let totalKrank = 0;

        let sumMonatsVkFaktoren = 0;
        let sumMonatsEffizienzen = 0;
        let sumMonatsAuslastungen = 0;

        activeMonths.forEach(m => {
            totalBedienBrutto += m.bedienumsatzBrutto;
            totalVkBrutto += m.vkUmsatzBrutto;
            totalGesamtBrutto += m.gesamtBrutto;
            totalGesamtNetto += m.gesamtNetto;
            totalSollNetto += m.mindestNetto;
            totalKostenNetto += m.kostendeckungNetto;
            totalBonusBedienung += m.bonusBedienung;
            totalBonusVK += m.bonusVK;
            totalActiveLohnfaktor += m.activeLohnfaktor || stammdaten.lohnfaktorSoll;
            totalUrlaub += m.urlaub || 0;
            totalKrank += m.krank || 0;
            if (m.isLocked) lockedCount++;

            sumMonatsVkFaktoren += safeDiv(m.vkUmsatzBrutto, m.bedienumsatzBrutto);
            sumMonatsEffizienzen += safeDiv(m.gesamtNetto, m.mindestNetto);
            
            const monatStdUmsatz = safeDiv(m.gesamtBrutto, stammdaten.monatsStunden);
            const monatSollUmsatzNetto = m.mindestNetto;
            const monatSollUmsatzBrutto = monatSollUmsatzNetto * mwstFaktor;
            const monatSollStdUmsatzBrutto = safeDiv(monatSollUmsatzBrutto, stammdaten.monatsStunden);
            sumMonatsAuslastungen += (auslastungsVorgabe / 100) * safeDiv(monatStdUmsatz, monatSollStdUmsatzBrutto);
        });

        const avgBedienBrutto = totalBedienBrutto / count;
        const avgVkBrutto = totalVkBrutto / count;
        const avgGesamtBrutto = totalGesamtBrutto / count;
        const avgGesamtNetto = totalGesamtNetto / count;
        const avgSollNetto = totalSollNetto / count;
        const avgKostenNetto = totalKostenNetto / count;
        const avgActiveLohnfaktor = totalActiveLohnfaktor / count;
        
        const totalBonusGesamt = totalBonusBedienung + totalBonusVK;
        const avgBonusGesamt = safeDiv(totalBonusGesamt, count);

        const stdUmsatzIstBrutto = safeDiv(avgGesamtBrutto, stammdaten.monatsStunden);
        const stdUmsatzSollBrutto = safeDiv(avgSollNetto * mwstFaktor, stammdaten.monatsStunden);
        const stdUmsatzSollBei75 = safeDiv(stdUmsatzSollBrutto, auslastungsVorgabe / 100);

        const deltaProStundeBrutto = stdUmsatzIstBrutto - stdUmsatzSollBrutto;
        const avgDeltaNetto = avgGesamtNetto - avgSollNetto;

        const zusatzumsatzWert = 30;
        const stundenProVerkauf = deltaProStundeBrutto < 0 ? safeDiv(zusatzumsatzWert, Math.abs(deltaProStundeBrutto)) : 0;

        const tagesStunden = safeDiv(stammdaten.wochenstunden, stammdaten.arbeitstage || 5);
        const upgradesProTag = stundenProVerkauf > 0 ? Math.ceil(tagesStunden / stundenProVerkauf) : 0;

        const tagesSollBrutto = stdUmsatzSollBrutto * tagesStunden;
        const ausfallKostenVerlust = totalKrank * tagesSollBrutto * (auslastungsVorgabe / 100);
        const urlaubUmsatzGutschrift = totalUrlaub * tagesSollBrutto * (auslastungsVorgabe / 100);

        // Ausfallquote: Krankheitstage / verfügbare Arbeitstage im gewählten Zeitraum
        const arbeitstageProMonat = safeDiv(stammdaten.monatsStunden, tagesStunden);
        const verfuegbareArbeitstage = count * arbeitstageProMonat;
        const ausfallquote = safeDiv(totalKrank, verfuegbareArbeitstage);

        const erarbeiteterStundenlohn = (!stammdaten.wochenstunden || stammdaten.wochenstunden <= 0 || !stammdaten.lohnfaktorSoll || stammdaten.lohnfaktorSoll <= 0) ? 0 : (avgGesamtBrutto / stammdaten.lohnfaktorSoll) / (stammdaten.wochenstunden * 4.33);

        // Erreichte Faktoren & explizite Differenzen zum Soll (Soll/Ist-Vergleich)
        const vkFaktor = sumMonatsVkFaktoren / count;
        const effizienz = sumMonatsEffizienzen / count;
        const umsatzauslastung = sumMonatsAuslastungen / count;
        // Lohnfaktor = Setup-LF × Zielerreichung — IDENTISCHE Netto-Basis wie die Ampel
        // (avgGesamtNetto vs avgSollNetto). Damit gilt streng:
        // Umsatzziel verfehlt ⇔ Faktor unter Setup | übertroffen ⇔ Faktor über Setup.
        const lohnBasis =
            (Number(stammdaten.effektiverBruttolohn) > 0
                ? Number(stammdaten.effektiverBruttolohn)
                : Number(stammdaten.gesamtlohnBrutto)) || 0;
        const setupLf = Number(stammdaten.lohnfaktorSoll) || 0;
        const zielErreichung = avgSollNetto > 0 ? safeDiv(avgGesamtNetto, avgSollNetto) : 0;
        const istLohnfaktor =
            setupLf > 0 && avgSollNetto > 0
                ? setupLf * zielErreichung
                : safeDiv(avgGesamtBrutto, lohnBasis);
        const lohnfaktorDiff = istLohnfaktor - setupLf;
        const vkZielFaktor = 0.10;
        const vkFaktorDiff = vkFaktor - vkZielFaktor;
        const effizienzDiff = effizienz - 1;
        const auslastungDiff = umsatzauslastung - (auslastungsVorgabe / 100);

        return {
            count, avgBedienBrutto, avgVkBrutto, avgGesamtBrutto, avgGesamtNetto, avgSollNetto, avgKostenNetto,
            vkFaktor, 
            effizienz, 
            umsatzauslastung,
            istLohnfaktor, lohnfaktorDiff, vkZielFaktor, vkFaktorDiff, effizienzDiff, auslastungDiff,
            stdUmsatzIstBrutto, stdUmsatzSollBrutto, stdUmsatzSollBei75,
            deltaProStundeBrutto, stundenProVerkauf, zusatzumsatzWert, upgradesProTag,
            avgDeltaNetto, avgBonusGesamt, totalBonusGesamt, lockedCount, avgActiveLohnfaktor,
            totalUrlaub, totalKrank, ausfallKostenVerlust, ausfallquote, verfuegbareArbeitstage, urlaubUmsatzGutschrift, tagesStunden, erarbeiteterStundenlohn
        };
    }, [startMonth, endMonth, rawMonthsData, stammdaten, mwstFaktor, auslastungsVorgabe]);

    const currentStatusKategorie = useMemo(() => {
        if (analysis.avgGesamtNetto >= analysis.avgSollNetto) return 'top';
        if (analysis.avgGesamtNetto >= analysis.avgKostenNetto) return 'ok';
        if (analysis.avgGesamtBrutto > 0) return 'warn';
        return 'crit';
    }, [analysis.avgGesamtNetto, analysis.avgSollNetto, analysis.avgKostenNetto, analysis.avgGesamtBrutto]);

    /** Marker auf 3-Zonen-Skala (analog Salon-Zeugnis) */
    const getMarkerPosition = () => {
        const be = analysis.avgKostenNetto;
        const ziel = analysis.avgSollNetto;
        const ist = analysis.avgGesamtNetto;

        if (ist <= be) {
            if (be === 0) return 0;
            return (ist / be) * 33.33;
        } else if (ist <= ziel) {
            const range = ziel - be;
            if (range <= 0) return 33.33;
            return 33.33 + ((ist - be) / range) * 33.33;
        } else {
            const range = ziel * 0.2;
            if (range <= 0) return 66.66;
            const overflow = Math.min(ist - ziel, range);
            return 66.66 + ((overflow / range) * 33.33);
        }
    };

    const handleStartKiPruefung = async () => {
        setIsAiLoading(true);
        setAiError(null);
        setShowAiAnalysis(false); 
        
        try {
            const functions = getFunctions(getApp(), 'europe-west1');
            const generateAi = httpsCallable(functions, 'generatePerformanceDiagnosticAi');
            
            const payloadMetrics = {
                ...analysis,
                statusKategorie: currentStatusKategorie,
                salonSchnitt: teamEffizienzIst || 0,
                salonDurchschnitt: teamEffizienzIst || 0,
                teamEfficiency: teamEffizienzIst || 0,
                teamEffizienzIst: teamEffizienzIst || 0,
                salonStundensatz: stammdaten.salonStundensatz || 0, 
                verkaufsStatus: analysis.vkFaktor >= 0.05 ? "Akzeptabel (Gelb)" : "Defizitär",
                vkFaktorFormatted: formatPercent(analysis.vkFaktor, 1)
            };

            const res = await generateAi({ 
                level: 'employee',
                targetName: title,
                metrics: payloadMetrics
            });
            
            const payload = (res.data as any)?.diagnostics;
            if (payload && Array.isArray(payload)) {
                setAiDiagnostics(payload);
                setShowAiAnalysis(true);
            } else {
                throw new Error("Das AI-Modell hat keine gültigen Diagnose-Daten zurückgeliefert.");
            }
        } catch (error: any) {
            console.error("KI-Prüfung konnte nicht durchgeführt werden:", error);
            setAiError(error?.message || "Unbekannter Systemfehler bei der Server-Kommunikation.");
        } finally {
            setIsAiLoading(false);
        }
    };

    if (!isOpen || !mounted) return null;

    const effLevel = getEvaluationLevel(analysis.effizienz, 'effizienz');
    const auslLevel = getEvaluationLevel(analysis.umsatzauslastung, 'auslastung');
    const vkLevel = getEvaluationLevel(analysis.vkFaktor, 'vk');
    const lfLevel = getLohnfaktorLevel(analysis.istLohnfaktor, stammdaten.lohnfaktorSoll || 0);
    const lfDiffClass = analysis.lohnfaktorDiff >= 0
        ? 'text-emerald-700 dark:text-emerald-400'
        : 'text-red-700 dark:text-red-400';

    const deltaSoll = analysis.avgDeltaNetto;
    const signedSoll = `${deltaSoll >= 0 ? '+' : ''}${formatCurrency(deltaSoll, currencyCode)}`;
    const deltaLabel = deltaSoll >= 0 ? 'Überschuss' : 'Defizit';

    const statusTitle = currentStatusKategorie === 'top' ? "Hervorragende Leistung!" :
                          currentStatusKategorie === 'ok' ? "Ziel knapp verfehlt, aber profitabel" :
                          currentStatusKategorie === 'warn' ? "Leistungshinweis" : "Dringender Handlungsbedarf";

    const statusDesc = currentStatusKategorie === 'top' ? `Der Mitarbeiter hat das Umsatzziel übertroffen und arbeitet sehr profitabel. Weiter so!` :
                         currentStatusKategorie === 'ok' ? `Der Mitarbeiter deckt seine Kosten, hat das Umsatzziel jedoch nicht ganz erreicht. Kontaktiere deinen Berater, um das Potenzial voll auszuschöpfen.` :
                         currentStatusKategorie === 'warn' ? `Der durchschnittliche Monatsumsatz liegt unter dem Kostendeckungsumsatz. Bitte kontaktiere deinen Betreuer.` :
                         `Achtung: Der Mitarbeiter ist nicht profitabel. Der Umsatz liegt unter dem Totpunkt (Break-Even).`;

    const ampelThumb = currentStatusKategorie === 'top' ? {
        text: 'Ziel übertroffen',
        color: 'text-emerald-500',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.993 0-1.854-.656-2.148-1.595l-2-6A2 2 0 018.25 10V5a2 2 0 012-2h2.5a2 2 0 012 2v5z" />
            </svg>
        ),
    } : currentStatusKategorie === 'ok' ? {
        text: 'Über Kostendeckung',
        color: 'text-amber-500',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        ),
    } : {
        text: 'Unter Kostendeckung',
        color: 'text-red-500',
        icon: (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.738 3h4.017c.993 0 1.854-.656 2.148-1.595l2 6A2 2 0 0116.75 14v5a2 2 0 01-2-2h-2.5a2 2 0 01-2-2v-5z" />
            </svg>
        ),
    };

    const gesamtZusatzTag = analysis.upgradesProTag * analysis.zusatzumsatzWert;

    const modalContent = (
        <div className="print-modal-wrapper fixed inset-0 z-[2147483647] flex justify-center items-center bg-stone-900/50 dark:bg-black/80 backdrop-blur-sm p-4 sm:p-8">
            
            <style>{`
                @media print {
                    body > *:not(.print-modal-wrapper) { display: none !important; }
                    html, body { height: auto !important; overflow: visible !important; background: white !important; margin: 0 !important; padding: 0 !important; }
                    .print-modal-wrapper { position: relative !important; display: block !important; height: auto !important; overflow: visible !important; background: white !important; padding: 0 !important; margin: 0 !important; }
                    .print-modal-inner { border: none !important; box-shadow: none !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; background: white !important; color: black !important; }
                    .print-hide { display: none !important; }
                    .print-scroll-area { overflow: visible !important; height: auto !important; max-height: none !important; }
                    table { border-collapse: collapse; width: 100%; }
                    th, td { border-bottom: 1px solid #cbd5e1 !important; padding: 12px 8px !important; }
                    tr { page-break-inside: avoid; }
                    textarea { resize: none !important; border: none !important; padding: 0 !important; background: transparent !important; color: black !important; outline: none !important; }
                }
            `}</style>

            <div className="print-modal-inner relative bg-pe-card dark:bg-black border border-pe-border dark:border-[#2a2a2a] carbon-panel carbon-weave-bg rounded-2xl w-full max-w-[1200px] max-h-[95vh] shadow-2xl flex flex-col text-pe-text dark:text-slate-200 font-sans overflow-hidden">
                
                {/* Modal-Header (nicht sticky — Zeugnis-Sticky liegt auf der Seite) */}
                <div className="flex-shrink-0 z-50 p-4 sm:p-5 bg-pe-tile-header dark:bg-[#08090b] border-b border-pe-border-strong carbon-content flex flex-wrap justify-between items-center gap-4 print-hide rounded-t-2xl">
                    <div className="flex items-center gap-3 min-w-0">
                        <Calendar className="text-pe-accent-text w-6 h-6 shrink-0" />
                        <div className="min-w-0">
                            <h3 className="font-bold text-lg text-pe-text leading-tight truncate">Performance-Analyse: <span className="text-pe-accent-text">{title}</span></h3>
                            {!isHJ2Freigegeben && <span className="text-[10px] text-orange-700 dark:text-orange-400 led-orange font-bold uppercase tracking-widest">2. Halbjahr gesperrt</span>}
                        </div>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-pe-tile-header px-3 py-2 rounded-xl border border-pe-border-strong shadow-inner">
                        <span className="text-sm font-bold text-pe-muted hidden sm:inline">Auswertung von:</span>
                        <select value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} className="bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] rounded p-1.5 text-pe-text text-sm font-bold focus:border-[#E6C299] outline-none cursor-pointer">
                            {validMonths.map((idx) => <option key={`start-${idx}`} value={idx} disabled={idx > endMonth}>{monateNamen[idx]}</option>)}
                        </select>
                        <span className="text-sm font-bold text-pe-muted">bis:</span>
                        <select value={endMonth} onChange={(e) => setEndMonth(Number(e.target.value))} className="bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] rounded p-1.5 text-pe-text text-sm font-bold focus:border-[#E6C299] outline-none cursor-pointer">
                            {validMonths.map((idx) => <option key={`end-${idx}`} value={idx} disabled={idx < startMonth}>{monateNamen[idx]}</option>)}
                        </select>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <div className="flex items-center gap-2 bg-pe-tile-header border border-pe-border-strong rounded-xl px-2 py-1.5">
                            <span className={`text-[10px] font-black uppercase tracking-wider ${!isSimpleView ? 'text-pe-accent-text' : 'text-pe-muted'}`}>Admin</span>
                            <button
                                type="button"
                                role="switch"
                                aria-checked={isSimpleView}
                                aria-label="Mitarbeiter-Ansicht umschalten"
                                onClick={() => setIsSimpleView((v) => !v)}
                                className={`relative w-12 h-7 rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-[#E6C299]/40 ${
                                    isSimpleView ? 'bg-[#E6C299]/25 border-[#E6C299]' : 'bg-pe-card dark:bg-[#15171e] border-slate-700'
                                }`}
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform shadow ${
                                        isSimpleView ? 'translate-x-5 bg-[#E6C299]' : 'translate-x-0 bg-slate-500'
                                    }`}
                                />
                            </button>
                            <span className={`text-[10px] font-black uppercase tracking-wider ${isSimpleView ? 'text-pe-accent-text' : 'text-pe-muted'}`}>Mitarbeiter</span>
                        </div>
                        {!showAiAnalysis && (
                            <button 
                                onClick={handleStartKiPruefung}
                                disabled={isAiLoading}
                                className={`flex items-center gap-2 bg-purple-950/30 border border-purple-500/50 hover:bg-purple-950/50 text-purple-700 dark:text-purple-400 led-purple font-bold text-sm px-4 py-2.5 rounded-xl transition-all shadow-md ${isAiLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                <BrainCircuit className={`w-4 h-4 ${isAiLoading ? 'animate-pulse' : ''}`} /> {isAiLoading ? 'Verbindung wird aufgebaut...' : 'KI-Prüfung starten'}
                            </button>
                        )}
                        <button
                            onClick={() => window.print()}
                            className="flex items-center gap-2 bg-pe-card dark:bg-[#15171e] border border-[#E6C299]/50 hover:border-[#E6C299] hover:bg-[#E6C299]/10 text-pe-accent-text font-bold text-sm px-5 py-2.5 rounded-xl transition-all shadow-[0_0_18px_rgba(230,194,153,0.22)] uppercase tracking-wide"
                        >
                            <Printer className="w-5 h-5" /> Ausdrucken
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsMgOpen(true)}
                            className="flex items-center gap-2 bg-pe-card dark:bg-[#15171e] border border-sky-500/40 hover:border-sky-400 hover:bg-sky-500/10 text-sky-700 dark:text-sky-300 font-bold text-sm px-4 py-2.5 rounded-xl transition-all uppercase tracking-wide"
                            title="Formblatt MG-01/02 vorbereiten"
                        >
                            <FileText className="w-5 h-5" />
                            <span className="hidden lg:inline">Entwicklungsgespräch vorbereiten</span>
                            <span className="lg:hidden">MG-01/02</span>
                        </button>
                        <button onClick={onClose} className="p-2.5 bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] hover:bg-pe-hover dark:hover:bg-[#222] text-pe-muted hover:text-pe-text rounded-full transition-colors">
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Scroll-Bereich */}
                <div className="flex-1 overflow-y-auto print-scroll-area p-6 md:p-10 space-y-8 carbon-content">
                    
                    <div className="hidden print-hide print:!flex justify-between items-end border-b-2 border-slate-800 pb-6 mb-8">
                        <div>
                            <h1 className="text-3xl font-black tracking-tight uppercase print:text-black">Performance-Zeugnis</h1>
                            <p className="text-base font-bold text-pe-muted mt-1 print:text-slate-600">Strategische Evaluierung & Mitarbeitergespräch</p>
                        </div>
                        <div className="text-right">
                            <h2 className="text-2xl font-bold text-pe-text uppercase print:text-black">{title}</h2>
                            <p className="text-sm font-bold text-pe-muted border border-pe-border-strong dark:border-slate-600 px-3 py-1 rounded-md inline-block mt-2 print:border-slate-300 print:text-slate-600">Auswertung: {monateNamen[startMonth]} bis {monateNamen[endMonth]}</p>
                        </div>
                    </div>

                    {aiError && (
                        <div className="bg-red-100 dark:bg-red-950/40 border border-red-500/50 rounded-xl p-5 shadow-inner flex items-start gap-4 animate-in fade-in zoom-in-95">
                            <span className="text-2xl mt-0.5">⚠️</span>
                            <div>
                                <h4 className="font-bold text-red-700 dark:text-red-400 led-red text-base mb-1">Server-Fehler: KI-Diagnose blockiert</h4>
                                <p className="text-sm text-red-200/90 font-mono mb-2">{aiError}</p>
                                <p className="text-xs text-red-300/60 leading-relaxed">
                                    Der Request konnte nicht erfolgreich verarbeitet werden. Entweder fehlen essenzielle Datenparameter für die Diagnose oder der KI-Service ist temporär nicht erreichbar. Die lokale Fallback-Auswertung wurde abgebrochen.
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
                        
                        <CustomTooltip text="Fasst die aktuelle Gesamtleistung zusammen. Zeigt auf einen Blick, ob der erwirtschaftete Umsatz ausreicht, um alle Lohn- und Fixkosten zu decken und das strategische Ziel zu erreichen.">
                            <div className={`p-5 rounded-xl border flex flex-col justify-between shadow-inner w-full h-full
                                ${currentStatusKategorie === 'top' ? 'bg-emerald-100/80 dark:bg-green-950/20 border-emerald-300 dark:border-green-500/50 text-emerald-950 dark:text-green-300 print:bg-white print:border-green-600 print:text-green-800' : ''}
                                ${currentStatusKategorie === 'ok' ? 'bg-amber-100/80 dark:bg-yellow-950/20 border-amber-300 dark:border-yellow-500/50 text-amber-950 dark:text-amber-400 print:bg-white print:border-yellow-600 print:text-yellow-800' : ''}
                                ${currentStatusKategorie === 'warn' ? 'bg-orange-100/80 dark:bg-orange-950/20 border-orange-300 dark:border-orange-500/50 text-orange-950 dark:text-orange-400 print:bg-white print:border-orange-600 print:text-orange-800' : ''}
                                ${currentStatusKategorie === 'crit' ? 'bg-red-100/80 dark:bg-red-950/20 border-red-300 dark:border-red-500/50 text-red-950 dark:text-red-400 print:bg-white print:border-red-600 print:text-red-800' : ''}
                            `}>
                                <div className="flex items-start gap-4">
                                    <div className={`shrink-0 flex flex-col items-center justify-center ${ampelThumb.color}`}>
                                        {ampelThumb.icon}
                                        <div className="text-[10px] font-bold text-center leading-tight mt-1">{ampelThumb.text}</div>
                                    </div>
                                    <div className="min-w-0">
                                        <h4 className={`text-base font-black uppercase tracking-wide mb-1 ${currentStatusKategorie === 'top' ? 'led-emerald' : currentStatusKategorie === 'ok' ? 'led-amber' : currentStatusKategorie === 'warn' ? 'led-orange' : 'led-red'}`}>{statusTitle}</h4>
                                        <p className="text-sm leading-relaxed italic">{statusDesc}</p>
                                        <div className="mt-3">
                                            <div className="text-[10px] font-bold uppercase tracking-widest opacity-70">{deltaLabel} Ø</div>
                                            <div className={`text-xl font-black font-mono-numbers leading-tight ${deltaSoll >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                                                {signedSoll}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {analysis.totalBonusGesamt > 0 && (
                                    <div className="mt-5 pt-4 border-t border-current/20 flex flex-wrap gap-x-12 gap-y-4">
                                        <div>
                                            <span className="block text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">Erarbeiteter Gesamtbonus (Periode)</span>
                                            <div className="text-2xl font-black font-mono font-mono-numbers tabular-nums">
                                                {formatCurrency(analysis.totalBonusGesamt, currencyCode)}
                                            </div>
                                        </div>
                                        <div>
                                            <span className="block text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">Ø Mtl. Bonus-Anspruch</span>
                                            <div className="text-2xl font-black font-mono font-mono-numbers tabular-nums">
                                                {formatCurrency(analysis.avgBonusGesamt, currencyCode)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Gibt exakt an, wie viel Euro Umsatz pro gearbeiteter Stunde fehlen (Defizit) oder als Überschuss generiert werden. Das 'Rezept zum Ausgleich' rechnet dies in konkrete tägliche Handlungen um.">
                            <div className="bg-pe-card dark:bg-black border border-pe-border-strong p-4 rounded-xl flex flex-col justify-between text-center shadow-inner print:bg-white print:border-slate-300 print:text-black w-full h-full">
                                <div>
                                    <div className="inline-flex items-center gap-1">
                                        <span className="block text-xs font-semibold text-pe-muted tracking-wider uppercase print:text-slate-500">STUNDEN-ABWEICHUNG (BRUTTO)</span>
                                        <Info className="w-3 h-3 text-pe-muted" />
                                    </div>
                                    <div className={`text-xl font-black font-mono font-mono-numbers mt-1 tabular-nums ${analysis.deltaProStundeBrutto >= 0 ? 'text-emerald-700 dark:text-emerald-400 led-emerald print:text-green-600' : 'text-red-700 dark:text-red-400 led-red print:text-red-600'}`}>
                                        {analysis.deltaProStundeBrutto >= 0 ? '+' : ''}{formatCurrency(analysis.deltaProStundeBrutto, currencyCode, 2)} <span className="text-xs text-pe-muted font-normal uppercase tracking-wider">/ Std</span>
                                    </div>
                                </div>

                                {analysis.deltaProStundeBrutto < 0 ? (
                                    <div className="mt-3 p-3 bg-pe-card rounded-lg border border-pe-border-strong text-left print:bg-slate-50 print:border-slate-300 shadow-inner">
                                        <h5 className="text-[11px] font-black uppercase tracking-wider text-pe-muted text-center border-b border-pe-border-strong pb-1.5 mb-1.5 print:text-slate-600 print:border-slate-200">Dein Rezept zum Ausgleich:</h5>
                                        <div className="text-xs text-pe-muted font-medium leading-relaxed text-center print:text-slate-800">
                                            Um das Defizit aufzuholen, sind mindestens <span className="font-bold text-red-800 dark:text-white bg-red-100 dark:bg-red-950/50 px-1 rounded font-mono font-mono-numbers print:bg-slate-200 print:text-black print:border print:border-slate-300">{analysis.upgradesProTag} gezielte Upgrades</span> (z.B. Verkauf) à <span className="font-bold text-pe-text font-mono font-mono-numbers bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] px-1 rounded print:bg-slate-200 print:text-black print:border print:border-slate-300">{formatCurrency(analysis.zusatzumsatzWert, currencyCode)}</span> pro Tag nötig.
                                            <div className="mt-2 pt-2 border-t border-pe-border-strong w-full text-[11px] text-pe-muted">
                                                Das entspricht <strong className="text-pe-accent-text text-[13px] tracking-wide font-mono-numbers">{formatCurrency(gesamtZusatzTag, currencyCode)}</strong> zusätzlichem Umsatz am Tag.
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mt-3 p-2.5 bg-emerald-100/80 dark:bg-emerald-950/30 rounded-lg border border-emerald-300 dark:border-emerald-500/30 text-center flex flex-col items-center justify-center print:bg-white print:border-green-300 shadow-inner">
                                        <Award className="w-5 h-5 text-emerald-700 dark:text-emerald-400 mb-1 print:text-green-600" />
                                        <span className="text-[10px] font-bold text-emerald-800 dark:text-emerald-500 uppercase tracking-widest mb-1 print:text-green-600">Zielvorgabe voll abgesichert!</span>
                                    </div>
                                )}
                            </div>
                        </CustomTooltip>
                        </div>

                        <div className="flex justify-center">
                        <CustomTooltip customClass="!h-auto w-full max-w-[260px]" text="Zeigt den tatsächlich erarbeiteten Stundenlohn basierend auf dem erwirtschafteten Umsatz und den gebuchten Stunden im Zeitraum.">
                            <div className={`p-3 rounded-xl border flex flex-col justify-center items-center text-center shadow-inner w-full
                                ${currentStatusKategorie === 'top' ? 'bg-emerald-100/80 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-500/50 text-emerald-950 dark:text-emerald-400 print:bg-white print:border-emerald-600 print:text-emerald-800' : ''}
                                ${currentStatusKategorie === 'ok' ? 'bg-amber-100/80 dark:bg-yellow-950/20 border-amber-300 dark:border-yellow-500/50 text-amber-950 dark:text-amber-400 print:bg-white print:border-yellow-600 print:text-yellow-800' : ''}
                                ${currentStatusKategorie === 'warn' ? 'bg-orange-100/80 dark:bg-orange-950/20 border-orange-300 dark:border-orange-500/50 text-orange-950 dark:text-orange-400 print:bg-white print:border-orange-600 print:text-orange-800' : ''}
                                ${currentStatusKategorie === 'crit' ? 'bg-red-100/80 dark:bg-red-950/20 border-red-300 dark:border-red-500/50 text-red-950 dark:text-red-400 print:bg-white print:border-red-600 print:text-red-800' : ''}
                            `}>
                                <div className="inline-flex items-center gap-1 mb-0.5">
                                    <span className="text-[10px] font-bold uppercase opacity-80">Ø Erarbeiteter Lohn</span>
                                    <Info className="w-3 h-3 opacity-60" />
                                </div>
                                <div className={`text-lg font-black font-mono font-mono-numbers tabular-nums leading-tight
                                    ${currentStatusKategorie === 'top' ? 'led-emerald' : currentStatusKategorie === 'ok' ? 'led-amber' : currentStatusKategorie === 'warn' ? 'led-orange' : 'led-red'}
                                `}>
                                    {formatCurrency(analysis.erarbeiteterStundenlohn, currencyCode, 2)} <span className="text-[10px] font-normal uppercase tracking-wider opacity-70">/ Std</span>
                                </div>
                                <div className="text-xs font-bold uppercase tracking-widest opacity-80 mt-1.5">
                                    Basis: {formatCurrency(stammdaten.stdLohnGrund, currencyCode, 2)}/Std
                                </div>
                            </div>
                        </CustomTooltip>
                        </div>
                    </div>

                    {!isSimpleView && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-pe-tile-header border border-pe-border-strong p-5 rounded-xl shadow-inner print:bg-white print:border-slate-300 print:text-black">
                        <div>
                            <span className="block text-xs font-semibold text-pe-muted uppercase tracking-wider mb-1">Arbeitszeit-Soll</span>
                            <span className="font-mono font-mono-numbers text-base font-black">{stammdaten.wochenstunden} Std/Wo <span className="text-sm font-medium text-pe-muted print:text-slate-500">({formatNumber(stammdaten.monatsStunden, 1)} Std/Mo)</span></span>
                        </div>
                        <div>
                            <span className="block text-xs font-semibold text-pe-muted uppercase tracking-wider mb-1">Grundgehalt</span>
                            <span className="font-mono font-mono-numbers text-base font-black">{formatCurrency(stammdaten.grundlohn, currencyCode)} <span className="text-sm font-medium text-pe-muted print:text-slate-500">({formatCurrency(stammdaten.stdLohnGrund, currencyCode, 2)}/Std)</span></span>
                        </div>
                        <div>
                            <span className="block text-xs font-semibold text-pe-muted uppercase tracking-wider mb-1">Gehalt (inkl. Fix-Bonus)</span>
                            <span className="font-mono font-mono-numbers text-base font-black">{formatCurrency(stammdaten.gesamtlohnBrutto, currencyCode)} <span className="text-sm font-medium text-pe-muted print:text-slate-500">({formatCurrency(stammdaten.stdLohnGesamt, currencyCode, 2)}/Std)</span></span>
                        </div>
                        <div>
                            <span className="block text-xs font-semibold text-pe-muted uppercase tracking-wider mb-1">Setup-Faktoren</span>
                            <span className="font-mono font-mono-numbers text-base font-black">Lohn: {stammdaten.lohnfaktorSoll.toFixed(2)} | Deckung: {stammdaten.kostendeckungsFaktor.toFixed(2)}</span>
                        </div>
                    </div>
                    )}

                    {isSimpleView && (
                    <div className="bg-pe-sunken border border-pe-border-strong p-5 rounded-xl shadow-inner print:bg-white print:border-slate-300">
                        <div className="flex flex-wrap justify-between items-end gap-4 mb-5">
                            <h4 className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-600">Rentabilitäts-Skala (Netto)</h4>
                            <div className="flex flex-wrap gap-3 justify-end">
                                <div className="text-right min-w-[110px]">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block">Ziel</span>
                                    <div className="bg-pe-card border border-pe-border-strong px-3 py-1.5 rounded-lg text-pe-text dark:text-slate-200 font-mono-numbers font-black text-base mt-1">
                                        {formatCurrency(analysis.avgSollNetto, currencyCode)}
                                    </div>
                                </div>
                                <div className="text-right min-w-[110px]">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block">Umsatz</span>
                                    <div className="bg-orange-100/80 dark:bg-orange-950/20 border border-orange-300 dark:border-orange-500/30 px-3 py-1.5 rounded-lg text-orange-950 dark:text-orange-400 font-mono-numbers font-black text-base mt-1">
                                        {formatCurrency(analysis.avgGesamtNetto, currencyCode)}
                                    </div>
                                </div>
                                <div className="text-right min-w-[110px]">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider block">Differenz</span>
                                    <div className={`border px-3 py-1.5 rounded-lg font-mono-numbers font-black text-base mt-1 ${
                                        analysis.avgDeltaNetto >= 0
                                            ? 'bg-emerald-100/80 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-500/30 text-emerald-950 dark:text-emerald-400'
                                            : 'bg-red-100/80 dark:bg-red-950/20 border-red-300 dark:border-red-500/30 text-red-950 dark:text-red-400'
                                    }`}>
                                        {analysis.avgDeltaNetto >= 0 ? '+' : ''}{formatCurrency(analysis.avgDeltaNetto, currencyCode)}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="relative h-10 bg-pe-sunken dark:bg-black rounded-lg overflow-hidden flex border border-pe-border dark:border-[#222] shadow-inner print:border print:border-slate-300">
                            <div className="h-full w-1/3 bg-red-100 dark:bg-red-950/40 border-r border-pe-border-strong flex items-center px-4 print:bg-red-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-red-700 dark:text-red-500 truncate print:text-red-700">Unter Deckung</span>
                            </div>
                            <div className="h-full w-1/3 bg-amber-100 dark:bg-amber-950/40 border-r border-pe-border-strong flex items-center px-4 print:bg-yellow-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-amber-700 dark:text-amber-500 truncate print:text-yellow-700">Zone Deckung</span>
                            </div>
                            <div className="h-full w-1/3 bg-emerald-100 dark:bg-emerald-950/40 flex items-center px-4 print:bg-emerald-100">
                                <span className="text-[10px] uppercase tracking-widest font-bold text-emerald-700 dark:text-emerald-400 truncate print:text-emerald-700">Zielbereich Profitabel</span>
                            </div>
                            <div
                                className="absolute top-0 bottom-0 w-2 bg-white border-2 border-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.8)] z-10 print:border-black transition-all duration-500"
                                style={{ left: `calc(${getMarkerPosition()}% - 4px)` }}
                            />
                        </div>
                    </div>
                    )}

                    {showAiAnalysis && !aiError && aiDiagnostics.length > 0 && (
                        <div className="border border-pe-border-strong rounded-2xl bg-pe-tile-header shadow-inner overflow-hidden print:bg-white print:border-slate-300 animate-in fade-in zoom-in-95 duration-300">
                            <div className="p-5 flex items-center gap-3 border-b border-pe-border-strong print:border-slate-300 print:bg-slate-50">
                                <BrainCircuit className="text-purple-700 dark:text-purple-400 led-purple w-6 h-6" />
                                <h3 className="font-bold text-lg text-purple-700 dark:text-purple-400 led-purple print:text-black">KI-Diagnose Report</h3>
                            </div>

                            <div className="p-6 space-y-4">
                                <p className="text-sm text-pe-muted mb-6 print:text-black">
                                    Die Auswertung der Leistungsdaten offenbart detaillierte Einblicke in die Profitabilität und Arbeitsweise. Bitte prüfe die folgenden Kausalitäten für das anstehende Mitarbeitergespräch:
                                </p>

                                {aiDiagnostics.map((diag, i) => (
                                    <KICard 
                                        key={i}
                                        type={diag.type} 
                                        title={diag.title}
                                        icon={diag.icon || "💡"}
                                    >
                                        {diag.text}
                                    </KICard>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* =========================================================
                        PERFORMANCE VITALS
                    ========================================================= */}
                    <div className={`grid grid-cols-1 gap-5 items-stretch mb-5 ${isSimpleView ? 'md:grid-cols-2 max-w-3xl mx-auto' : 'md:grid-cols-2 xl:grid-cols-4'}`}>
                        
                        {!isSimpleView && (
                        <CustomTooltip text="Bewertet die reine Leistungsdichte während der Arbeitszeit. Sie macht Mitarbeiter fair vergleichbar – unabhängig von Gehalt oder individuellem Ziel – indem sie misst, wie fleißig die reine Anwesenheit in Umsatz verwandelt wird.">
                            <div className={`bg-pe-card border ${effLevel.borderClass} ${effLevel.bgClass} p-5 rounded-xl text-center shadow-inner flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-1 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Ø Effizienz</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-3xl font-black font-mono font-mono-numbers mb-2 tabular-nums ${effLevel.textClass} ${effLevel.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatPercent(analysis.effizienz, 1)}
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${effLevel.badgeBg} ${effLevel.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {effLevel.label}
                                </span>
                                <span className="text-[9px] text-pe-muted font-bold uppercase mt-2 print:text-slate-400">(Ref: 100%)</span>
                                <span className={`text-[11px] font-black font-mono-numbers mt-1 ${effLevel.textClass}`}>
                                    Differenz: {formatProzentPunkte(analysis.effizienzDiff)}
                                </span>
                            </div>
                        </CustomTooltip>
                        )}

                        {!isSimpleView && (
                        <CustomTooltip text="Misst, wie lukrativ die Arbeitszeit genutzt wird, um das persönliche Gehalts-Ziel zu erreichen. Es geht nicht um die schiere Menge an Kunden, sondern um hochwertige Dienstleistungen und clevere Zusatzverkäufe pro Stunde.">
                            <div className={`bg-pe-card border ${auslLevel.borderClass} ${auslLevel.bgClass} p-5 rounded-xl text-center shadow-inner flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-1 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Ø Umsatzauslastung</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-3xl font-black font-mono font-mono-numbers mb-2 tabular-nums ${auslLevel.textClass} ${auslLevel.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatPercent(analysis.umsatzauslastung, 1)}
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${auslLevel.badgeBg} ${auslLevel.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {auslLevel.label}
                                </span>
                                <span className="text-[9px] text-pe-muted font-bold uppercase mt-2 print:text-slate-400">(Ziel: {auslastungsVorgabe}%)</span>
                                <span className={`text-[11px] font-black font-mono-numbers mt-1 ${auslLevel.textClass}`}>
                                    Differenz: {formatProzentPunkte(analysis.auslastungDiff)}
                                </span>
                            </div>
                        </CustomTooltip>
                        )}

                        <CustomTooltip text="Setup-Lohnfaktor × Zielerreichung (IST-Netto ÷ Soll-Netto — gleiche Ampel-Basis). Ziel verfehlt → Faktor unter Setup (nie grün). Ziel übertroffen → Faktor darüber.">
                            <div className={`bg-pe-card border ${lfLevel.borderClass} ${lfLevel.bgClass} p-5 rounded-xl text-center shadow-inner flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-1 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Ø Lohnfaktor erreicht</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-3xl font-black font-mono font-mono-numbers mb-2 tabular-nums ${lfLevel.textClass} ${lfLevel.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatNumber(analysis.istLohnfaktor, 2)} x
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${lfLevel.badgeBg} ${lfLevel.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {lfLevel.label}
                                </span>
                                <span className="text-[9px] text-pe-muted font-bold uppercase mt-2 print:text-slate-400">(Ziel: {formatNumber(stammdaten.lohnfaktorSoll, 2)} x)</span>
                                <span className={`text-[11px] font-black font-mono-numbers mt-1 ${lfDiffClass}`}>
                                    Differenz: {analysis.lohnfaktorDiff >= 0 ? '+' : ''}{formatNumber(analysis.lohnfaktorDiff, 2)} x
                                </span>
                            </div>
                        </CustomTooltip>

                        <CustomTooltip text="Zeigt den reinen Produktverkauf (VK) im prozentualen Verhältnis zur erbrachten Dienstleistung.">
                            <div className={`bg-pe-card border ${vkLevel.borderClass} ${vkLevel.bgClass} p-5 rounded-xl text-center shadow-inner flex flex-col justify-center items-center print:bg-white print:border-slate-300 h-full`}>
                                <div className="inline-flex items-center gap-1 mb-2">
                                    <span className="text-xs font-semibold text-pe-muted uppercase tracking-wider print:text-slate-500">Verkaufsfaktor</span>
                                    <Info className="w-3 h-3 text-pe-muted" />
                                </div>
                                <div className={`text-3xl font-black font-mono font-mono-numbers mb-2 tabular-nums ${vkLevel.textClass} ${vkLevel.shadowClass} print:text-black print:drop-shadow-none`}>
                                    {formatPercent(analysis.vkFaktor, 1)}
                                </div>
                                <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${vkLevel.badgeBg} ${vkLevel.textClass} print:bg-slate-100 print:border-slate-400`}>
                                    {vkLevel.label}
                                </span>
                                <span className="text-[9px] text-pe-muted font-bold uppercase mt-2 print:text-slate-400">(Ziel: {formatPercent(analysis.vkZielFaktor, 1)})</span>
                                <span className={`text-[11px] font-black font-mono-numbers mt-1 ${vkLevel.textClass}`}>
                                    Differenz: {formatProzentPunkte(analysis.vkFaktorDiff)}
                                </span>
                            </div>
                        </CustomTooltip>

                    </div>

                    {/* =========================================================
                        ABSENZEN & RISIKO
                    ========================================================= */}
                    <div className={`grid grid-cols-1 gap-5 items-stretch ${isSimpleView ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                        
                        {/* Urlaub */}
                        <CustomTooltip text="Dokumentierte Urlaubstage im gewählten Zeitraum und kalkulatorisch gutgeschriebener Umsatz.">
                            <div className="bg-pe-card border border-pe-border-strong p-4 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full">
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Calendar className="text-amber-400 w-4 h-4 print:text-amber-600" />
                                    <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest print:text-amber-600">Urlaubstage</span>
                                </div>
                                <div className="text-3xl text-center font-black font-mono font-mono-numbers text-amber-700 dark:text-amber-400 led-amber print:text-amber-700 py-2">
                                    {formatNumber(analysis.totalUrlaub, 0)} <span className="text-sm font-sans font-bold">Tage</span>
                                </div>
                                <div className="text-center text-sm font-mono font-mono-numbers font-bold text-amber-300/90 print:text-amber-700">
                                    +{formatCurrency(analysis.urlaubUmsatzGutschrift, currencyCode, 0)}
                                </div>
                                <span className="text-[10px] text-center text-amber-500/70 mt-1 font-bold print:text-amber-600">Gutgeschriebener Umsatz</span>
                            </div>
                        </CustomTooltip>

                        {/* Krankheit kompakt: Tage + Quote + Verlust */}
                        <CustomTooltip text="Dokumentierte Krankheitstage, Ausfallquote (Krankheitstage ÷ verfügbare Arbeitstage im Zeitraum) und kalkulatorischer Umsatzverlust.">
                            <div className={`bg-pe-card border ${analysis.totalKrank > 0 ? 'border-red-500/30' : 'border-pe-border-strong'} p-4 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full`}>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <Stethoscope className={`w-4 h-4 ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-400 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-400 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Krankheitstage</span>
                                </div>
                                <div className={`text-3xl text-center font-black font-mono font-mono-numbers py-2 ${analysis.totalKrank > 0 ? 'text-red-700 dark:text-red-400 led-red print:text-red-700' : 'text-pe-muted dark:text-slate-300 print:text-slate-600'}`}>
                                    {formatNumber(analysis.totalKrank, 0)} <span className="text-sm font-sans font-bold">Tage</span>
                                </div>
                                <div className={`text-center text-base font-black font-mono font-mono-numbers ${analysis.totalKrank > 0 ? 'text-red-500 dark:text-red-400 print:text-red-700' : 'text-pe-muted'}`}>
                                    {formatPercent(analysis.ausfallquote, 1)}
                                </div>
                                <span className={`text-[10px] text-center font-bold ${analysis.totalKrank > 0 ? 'text-red-700/80 dark:text-red-500/70 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>
                                    Ausfallquote
                                    {analysis.verfuegbareArbeitstage > 0
                                        ? ` · ${formatNumber(analysis.verfuegbareArbeitstage, 0)} AT-Tage`
                                        : ''}
                                </span>
                                <div className={`text-center text-sm font-mono font-mono-numbers font-bold mt-2 ${analysis.totalKrank > 0 ? 'text-red-400 print:text-red-700' : 'text-pe-muted'}`}>
                                    {analysis.totalKrank > 0 ? `−${formatCurrency(analysis.ausfallKostenVerlust, currencyCode, 0)}` : formatCurrency(0, currencyCode, 0)}
                                </div>
                                <span className={`text-[10px] text-center mt-1 font-bold ${analysis.totalKrank > 0 ? 'text-red-700/80 dark:text-red-500/70 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Kalkulatorischer Umsatzverlust</span>
                            </div>
                        </CustomTooltip>

                        {!isSimpleView && (
                        <CustomTooltip text="Berechnet die finanziellen Einbußen im Umsatz, die durch die dokumentierten Krankheitstage entstanden sind.">
                            <div className={`bg-pe-card border ${analysis.totalKrank > 0 ? 'border-red-500/30' : 'border-pe-border-strong'} p-4 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full`}>
                                <div className="flex items-center justify-center gap-2 mb-2">
                                    <TrendingDown className={`w-4 h-4 ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`} />
                                    <span className={`text-[10px] font-bold uppercase tracking-widest ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Ø Umsatzverlust (Krank)</span>
                                </div>
                                <div className={`text-3xl text-center font-black font-mono font-mono-numbers py-2 ${analysis.totalKrank > 0 ? 'text-red-600 dark:text-red-500 led-red print:text-red-700' : 'text-pe-muted dark:text-slate-300 print:text-slate-600'}`}>
                                    {analysis.totalKrank > 0 ? `-${formatCurrency(analysis.ausfallKostenVerlust, currencyCode, 0)}` : formatCurrency(0, currencyCode, 0)}
                                </div>
                                <span className={`text-[10px] text-center mt-1 font-bold ${analysis.totalKrank > 0 ? 'text-red-700/80 dark:text-red-500/70 print:text-red-600' : 'text-pe-muted print:text-slate-500'}`}>Kalkulatorischer Ausfall</span>
                            </div>
                        </CustomTooltip>
                        )}

                    </div>


                    <div className={`grid grid-cols-1 gap-6 items-stretch ${isSimpleView ? '' : 'md:grid-cols-3'}`}>
                        <CustomTooltip text="Stundenumsatz: IST, Ziel (100 %), Soll bei Auslastungsvorgabe und Differenz zum 100 %-Ziel." customClass={isSimpleView ? 'max-w-lg mx-auto w-full' : ''}>
                            <div className="bg-pe-card border border-pe-border-strong p-4 rounded-xl shadow-inner print:bg-white print:border-slate-300 flex flex-col justify-between h-full">
                                <span className="text-sm font-bold text-pe-muted uppercase block text-center mb-3 tracking-wide">Ø Stundenumsatz (Brutto)</span>
                                
                                <div className="flex justify-between items-end border-b border-pe-border-strong pb-2 mb-2">
                                    <span className="text-xs font-bold text-pe-text print:text-black">IST-Leistung:</span>
                                    <span className="text-xl font-black font-mono font-mono-numbers text-pe-accent-text led-gold print:text-slate-900">{formatCurrency(analysis.stdUmsatzIstBrutto, currencyCode, 0)}</span>
                                </div>

                                <div className="flex justify-between items-center border-b border-pe-border-strong pb-1.5 mb-1.5">
                                    <span className="text-xs font-semibold text-pe-muted">Ziel-Leistung (100%):</span>
                                    <span className="text-sm font-bold font-mono font-mono-numbers text-pe-text dark:text-slate-200 print:text-slate-700">{formatCurrency(analysis.stdUmsatzSollBrutto, currencyCode, 0)}</span>
                                </div>
                                
                                <div className="flex justify-between items-start gap-3 border-b border-pe-border-strong pb-2 mb-2">
                                    <div className="min-w-0 flex-1">
                                        <span className="text-sm font-bold text-pe-muted dark:text-slate-300 block">Soll bei {auslastungsVorgabe}%:</span>
                                        <span className="text-xs text-pe-muted leading-snug block mt-0.5">
                                            Ø nötig für Ziel bei {auslastungsVorgabe}% Auslastung
                                        </span>
                                    </div>
                                    <span className="text-base font-bold font-mono font-mono-numbers text-amber-700 dark:text-amber-300 print:text-amber-700 shrink-0 pt-0.5">
                                        {formatCurrency(analysis.stdUmsatzSollBei75, currencyCode, 0)}
                                    </span>
                                </div>

                                <div className="my-3">
                                    <div className="h-3.5 rounded-full bg-pe-sunken dark:bg-black border border-pe-border-strong overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all ${auslLevel.barClass}`}
                                            style={{
                                                width: `${Math.min(100, Math.max(0, safeDiv(analysis.stdUmsatzIstBrutto, analysis.stdUmsatzSollBei75 || 1) * 100))}%`,
                                            }}
                                        />
                                    </div>
                                    <div className={`mt-2 text-sm text-center font-black uppercase tracking-wide ${auslLevel.textClass}`}>
                                        {Math.round(safeDiv(analysis.stdUmsatzIstBrutto, analysis.stdUmsatzSollBei75 || 1) * 100)}% der nötigen {auslastungsVorgabe}%-Auslastung erreicht
                                    </div>
                                </div>

                                {!isSimpleView && stammdaten.salonStundensatz !== undefined && stammdaten.salonStundensatz > 0 && (
                                    <>
                                        <div className="flex justify-between items-center border-b border-pe-border-strong pb-1.5 mb-1.5 bg-blue-100 dark:bg-blue-950/30 px-2 -mx-2 rounded">
                                            <span className="text-[10px] font-bold text-blue-300">SALON Ø/STD:</span>
                                            <span className="text-sm font-bold font-mono font-mono-numbers text-blue-300">{formatCurrency(stammdaten.salonStundensatz, currencyCode, 0)}</span>
                                        </div>
                                        <div className="flex justify-between items-center border-b border-pe-border-strong pb-1.5 mb-1.5 px-2 -mx-2">
                                            <span className="text-xs font-semibold text-pe-muted">DIFF. (ZU {auslastungsVorgabe}%):</span>
                                            <span className={`text-sm font-bold font-mono font-mono-numbers ${(stammdaten.salonStundensatz - analysis.stdUmsatzSollBei75) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {(stammdaten.salonStundensatz - analysis.stdUmsatzSollBei75) >= 0 ? '+' : ''}{formatCurrency(stammdaten.salonStundensatz - analysis.stdUmsatzSollBei75, currencyCode, 0)}
                                            </span>
                                        </div>
                                    </>
                                )}
                                
                                <div className="flex justify-between items-center mt-2 pt-3 border-t border-pe-border-strong">
                                    <span className="text-xs font-black uppercase text-pe-muted dark:text-slate-300 tracking-wide">Differenz zu Ziel (100%):</span>
                                    <span className={`text-lg font-black font-mono font-mono-numbers ${analysis.deltaProStundeBrutto >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                        {analysis.deltaProStundeBrutto >= 0 ? '+' : ''}{formatCurrency(analysis.deltaProStundeBrutto, currencyCode, 2)}
                                    </span>
                                </div>
                            </div>
                        </CustomTooltip>

                        {!isSimpleView && (
                        <CustomTooltip text="Eine Gegenüberstellung der tatsächlich erbrachten Leistung (IST) mit den kalkulierten Zielwerten (SOLL)." customClass="md:col-span-2">
                            <div className="border border-pe-border-strong bg-pe-card rounded-xl overflow-hidden shadow-inner print:border-slate-300 print:rounded-none h-full flex flex-col">
                                <table className="w-full text-base font-sans h-full">
                                    <thead className="bg-pe-sunken border-b border-pe-border-strong text-pe-muted print:bg-slate-100 print:text-black">
                                        <tr>
                                            <th className="p-4 text-left font-black uppercase text-sm">Evaluierungsmatrix (Mtl. Ø)</th>
                                            <th className="p-4 text-right font-black uppercase text-sm">Dienstleistung</th>
                                            <th className="p-4 text-right font-black uppercase text-sm">Verkauf</th>
                                            <th className="p-4 text-right font-black uppercase text-sm">Gesamtertrag (Brutto)</th>
                                            <th className="p-4 text-right font-black uppercase text-sm">Reales Netto</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-[#333] font-bold print:divide-slate-200 print:text-black">
                                        <tr className="hover:bg-pe-hover dark:hover:bg-[#1a1a1a] transition-colors print:bg-white">
                                            <td className="p-4 text-pe-muted dark:text-slate-300">Erreichte Leistung (IST)</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums">{formatCurrency(analysis.avgBedienBrutto, currencyCode)}</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums">{formatCurrency(analysis.avgVkBrutto, currencyCode)}</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums font-black text-orange-700 dark:text-orange-400 led-orange">{formatCurrency(analysis.avgGesamtBrutto, currencyCode)}</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums font-black text-emerald-700 dark:text-emerald-400 led-emerald">{formatCurrency(analysis.avgGesamtNetto, currencyCode)}</td>
                                        </tr>
                                        <tr className="bg-pe-sunken dark:bg-black print:bg-slate-50">
                                            <td className="p-4 text-pe-muted">Strategisches Soll-Ziel (SOLL)</td>
                                            <td className="p-4 text-right text-pe-muted font-mono font-mono-numbers">—</td>
                                            <td className="p-4 text-right text-pe-muted font-mono font-mono-numbers">—</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums text-pe-muted">{formatCurrency(analysis.avgSollNetto * mwstFaktor, currencyCode)}</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums text-pe-muted">{formatCurrency(analysis.avgSollNetto, currencyCode)}</td>
                                        </tr>
                                        <tr className="border-t-2 border-pe-border-strong dark:border-[#444] print:bg-white print:border-slate-300">
                                            <td className="p-4 text-pe-muted dark:text-slate-300">Reine Mindest-Kostendeckung</td>
                                            <td colSpan={2} className="p-4 text-right text-sm text-pe-muted italic">Lohn- & Fixkosten (Break-Even):</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums text-pe-accent-text">{formatCurrency(analysis.avgKostenNetto * mwstFaktor, currencyCode)}</td>
                                            <td className="p-4 text-right font-mono font-mono-numbers tabular-nums text-pe-accent-text">{formatCurrency(analysis.avgKostenNetto, currencyCode)}</td>
                                        </tr>
                                        
                                        <tr className={`border-t-4 border-pe-border-strong print:border-slate-300 ${currentStatusKategorie === 'top' ? 'bg-emerald-100/60 dark:bg-emerald-950/10 print:bg-green-50/50' : 'bg-red-100/60 dark:bg-red-950/10 print:bg-red-50/50'}`}>
                                            <td className={`p-4 font-black flex items-center gap-2 ${currentStatusKategorie === 'top' ? 'text-emerald-700 dark:text-emerald-400 led-emerald' : 'text-red-700 dark:text-red-400 led-red'}`}>
                                                {currentStatusKategorie === 'top' ? <Award className="w-5 h-5" /> : <TrendingDown className="w-5 h-5" />} 
                                                {currentStatusKategorie === 'top' ? 'Erwirtschafteter Überschuss (mtl.)' : 'Ø Netto-Abweichung (mtl.)'}
                                            </td>
                                            <td colSpan={2} className="p-4 text-right text-sm text-pe-muted italic">Differenz zum Plan-Soll im Zeitraum:</td>
                                            <td colSpan={2} className={`p-4 text-right font-mono font-mono-numbers tabular-nums font-black text-xl ${currentStatusKategorie === 'top' ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(analysis.avgDeltaNetto, currencyCode, 0)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </CustomTooltip>
                        )}
                    </div>

                    <div className="pt-6 border-t-2 border-pe-border-strong space-y-4 print:border-slate-300 print:pt-8 print:break-inside-avoid">
                        <div>
                            <h4 className="text-sm font-black uppercase tracking-wider text-pe-muted mb-2">Gemeinsam vereinbarte Optimierungs-Ziele & Maßnahmen:</h4>
                            <textarea 
                                value={vereinbarungText}
                                onChange={(e) => setVereinbarungText(e.target.value)}
                                placeholder="Klicke hier, um vereinbarte Ziele, Schulungen oder Maßnahmen direkt einzutragen..."
                                rows={4}
                                className="w-full mt-2 bg-pe-input dark:bg-black border border-pe-border-strong dark:border-[#444] rounded-xl p-4 text-pe-muted dark:text-slate-300 placeholder-stone-400 dark:placeholder-slate-600 focus:outline-none focus:border-[#E6C299] focus:ring-1 focus:ring-[#E6C299] print:bg-transparent print:border-none print:text-black print:p-0 print:h-auto print:resize-none"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-16 pt-12 text-center text-sm font-bold text-pe-muted print:text-black print:mt-12">
                            <div className="space-y-2">
                                <div className="border-b-2 border-pe-border-strong w-full h-8 print:border-black"></div>
                                <span>Datum & Unterschrift Mitarbeiter/in</span>
                            </div>
                            <div className="space-y-2">
                                <div className="border-b-2 border-pe-border-strong w-full h-8 print:border-black"></div>
                                <span>Datum & Unterschrift Saloninhaber/in</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );

    return (
        <>
            {createPortal(modalContent, document.body)}
            <MitarbeiterGespraechModal
                isOpen={isMgOpen}
                onClose={() => setIsMgOpen(false)}
                employeeName={title}
                inhaberName={inhaberName}
                zeitraum={`${monateNamen[startMonth]} bis ${monateNamen[endMonth]}`}
                currencyCode={currencyCode}
                kennzahlen={{
                    multiplikator: stammdaten.lohnfaktorSoll,
                    istMultiplikator: analysis.istLohnfaktor,
                    vkQuote: analysis.vkFaktor,
                    vkZiel: analysis.vkZielFaktor,
                    umsatzauslastung: analysis.umsatzauslastung,
                    auslastungZiel: auslastungsVorgabe / 100,
                    basisStdUmsatz: analysis.stdUmsatzSollBrutto,
                    istStdUmsatz: analysis.stdUmsatzIstBrutto,
                    stdUmsatz75: analysis.stdUmsatzSollBei75,
                    umsatzZielNetto: analysis.avgSollNetto,
                    umsatzIstNetto: analysis.avgGesamtNetto,
                }}
            />
        </>
    );
};

export default ExecutiveSummaryModal;